/**
 * A level of a machine, a card on its upgrades page: its picture, its number and where the machine
 * stands to it (done, current, next, later), and what it holds, takes in and reaches, a value it
 * raises in the machine's colour. The next level adds what upgrading to it takes (NextLevel.tsx).
 */
import type { JSX } from 'react';

import type { TrackLevel, Upgrade, UpgradeStat } from '@game/upgrade/upgrades';
import { t, type MessageKey } from '@i18n/index';
import type { HexLook } from '@ui/art/hexArt';
import { HexPicture } from '@ui/components/HexPicture';
import { Icon } from '@ui/icons';

import { STAT_LABEL, unitText, valueText } from './levelText';
import { NextLevel } from './NextLevel';

import './level.css';

const CHIP: Readonly<Record<TrackLevel['state'], MessageKey>> = {
  past: 'upgrade.past',
  current: 'upgrade.current',
  next: 'upgrade.next',
  later: 'upgrade.later',
};

export function LevelCard({
  level,
  stats,
  upgrade,
  look,
  tile,
  name,
}: {
  level: TrackLevel;
  /** The values its role shows, in order. */
  stats: readonly UpgradeStat[];
  /** The machine's upgrade: the next level's card shows it. */
  upgrade: Upgrade | null;
  look: HexLook;
  tile: number;
  name: string;
}): JSX.Element {
  return (
    <article className={`lcard ${level.state}`} data-level={level.level} data-state={level.state}>
      <HexPicture look={look} />
      <div className="lbody">
        <header>
          <span className="lv">
            <Icon id="star" />
            {t('info.level', { n: level.level })}
          </span>
          <span className="chip">
            {level.state === 'past' && <Icon id="check" />}
            {t(CHIP[level.state])}
          </span>
        </header>
        <dl className="lstats">
          {stats.map((stat) => {
            const unit = unitText(stat);
            const up = (level.gains[stat] ?? 0) > 0;
            return (
              <div key={stat} data-stat={stat} className={up ? 'up' : undefined}>
                <dt>{t(STAT_LABEL[stat])}</dt>
                <dd>
                  {valueText(stat, level.values[stat] ?? 0)}
                  {unit && <small> {unit}</small>}
                  {up && <Icon id="up" />}
                </dd>
              </div>
            );
          })}
        </dl>
      </div>
      {level.state === 'next' && upgrade && <NextLevel upgrade={upgrade} tile={tile} name={name} />}
      {level.state === 'later' && (
        <p className="after">{t('upgrade.after', { n: level.level - 1 })}</p>
      )}
    </article>
  );
}
